<template>
    <ion-header>
        <ion-toolbar>
            <ion-buttons slot="start">
                <ion-back-button></ion-back-button>
            </ion-buttons>
            <ion-title>Seleziona l'indirizzo</ion-title>

            <ion-buttons slot="end">
                <ion-nav-link router-direction="forward" :component="CostumerForm" :componentProps="cleanedData">
                    <ion-button :disabled="loading" :strong="true">Salva</ion-button>
                </ion-nav-link>
            </ion-buttons>
        </ion-toolbar>
        <ion-toolbar>
            <ion-searchbar ref="searchbar" id="places-autocomplete-searchbar" v-model="search"
                           placeholder="Cerca un indirizzo..." @ionCancel="clearSelection"
                           @ionInput="clearSelection"></ion-searchbar>
        </ion-toolbar>
    </ion-header>
    <ion-content :scroll-y="false"
                 style="--background: var(--ion-item-background);">
        <div style="height: 100%; display: flex; flex-direction: column">
            <div style="flex-grow: 1; overflow: scroll; position: relative;" class="ion-padding">
                <template v-if="!loading">
                    <template v-if="results.length > 0">
                        <ion-item v-for="place in results"
                                  style="--inner-padding-bottom: 8px; --inner-padding-top: 8px; --padding-top: 0px; --padding-bottom: 0px;"
                                  class="relative ion-activatable ripple-parent"
                                  @click="onPlaceSelect(place)">
                            <ion-checkbox
                                :checked="selected ? selected.prediction.place_id == place.prediction.place_id : false"
                                style="pointer-events: none"
                                :aria-label="`select trap-${place.prediction.place_id}`" slot="start"></ion-checkbox>
                            <div>
                                <ion-label>{{ place.prediction.structured_formatting.main_text }}</ion-label>
                                <small v-if="place.prediction.structured_formatting.secondary_text" class="block">
                                    <ion-note>
                                        {{ place.prediction.structured_formatting.secondary_text }}
                                    </ion-note>
                                </small>
                            </div>
                            <ion-ripple-effect></ion-ripple-effect>
                        </ion-item>
                    </template>
                    <template v-else>
                        <div class="flex justify-center ion-margin-top">
                            <ion-note>
                                <small><em>Nessun risultato</em></small>
                            </ion-note>
                        </div>
                    </template>
                </template>
                <template v-else>
                    <ion-item class="costumer-list-item" v-for="_ in [1, 2, 3, 4, 5, 6, 7, 8, 9]">
                        <ion-label>
                            <h3>
                                <ion-skeleton-text :animated="true" style="width: 80%;"></ion-skeleton-text>
                            </h3>
                            <p>
                                <ion-skeleton-text :animated="true" style="width: 60%;"></ion-skeleton-text>
                            </p>
                            <p>
                                <ion-skeleton-text :animated="true" style="width: 30%;"></ion-skeleton-text>
                            </p>
                        </ion-label>
                    </ion-item>
                </template>
                <!--                <ion-fab-button style="position: absolute; right: 16px; bottom: 16px" @click="getCurrentPosition">
                                    <ion-icon v-if="!positionLoading" :icon="location"></ion-icon>
                                    <ion-spinner v-else></ion-spinner>
                                </ion-fab-button>-->
            </div>
            <div>
                <map-box height="35vh" borderRadius="0"
                         :markers-data="mapMarkers"
                         :disable-interactions="false"></map-box>
            </div>
        </div>


    </ion-content>
</template>


<script setup lang="ts">
import {
    IonContent,
    IonHeader,
    IonTitle,
    IonToolbar,
    IonButtons,
    IonButton,
    IonSearchbar,
    IonNavLink,
    IonIcon,
    IonNote,
    IonRippleEffect,
    IonItem,
    IonSpinner,
    IonLabel,
    IonCheckbox,
    IonFabButton,
    modalController, IonSkeletonText, IonBackButton, useIonRouter, NavComponentWithProps,
} from '@ionic/vue';
import {computed, inject, onMounted, PropType, reactive, ref, watch} from "vue";
import {debounce} from "@/utility";
import MapBox from "@/components/MapBox.vue";
import {IExtendedPlace} from "@/types/maps";

import {ICostumer} from "@/types/costumer";
import CostumerForm from "@/components/modals/CostumerForm.vue";

import {} from "@/stores/costumers";
import {Geolocation} from '@capacitor/geolocation';
import { IExtendedMarkerOptions } from '../../types/maps';
import MarkerOptions = google.maps.MapOptions;
import Marker = google.maps.Marker;
import LatLngLiteral = google.maps.LatLngLiteral;


const props = defineProps({
    costumer: {
        type: Object as PropType<ICostumer>,
        required: true,
    },
})


const search = ref<string>('');
const loading = ref<boolean>(false);
const placeServiceSessionToken = new google.maps.places.AutocompleteSessionToken();
const placesService = new google.maps.places.AutocompleteService();
const geocoderService = new google.maps.Geocoder();
const results = reactive<IExtendedPlace[]>([]);
const mapMarkers = reactive<MarkerOptions[]>([]);
const selected = ref<IExtendedPlace | undefined>(undefined);
const currentPosition = ref<LatLngLiteral | undefined>()
const positionLoading = ref<boolean>(false)
// const mapMarkers = computed(() => results.map((item) => ({coordinate: {lat: item.geocoded.geometry.location.lat(), lng: item.geocoded.geometry.location.lng()}} as Marker)))
const cleanedData = computed(() => {
    if (selected.value) {
        return {
            costumer: {
                ...props.costumer,
                address: selected.value.prediction.structured_formatting.main_text,
                latitude: selected.value.geocoded.geometry.location.lat(),
                longitude: selected.value.geocoded.geometry.location.lng()
            },
        }
    } else {
        return {costumer: props.costumer}
    }
})

const getCurrentPosition = async () => {
    positionLoading.value = true
    const coordinates = await Geolocation.getCurrentPosition();
    currentPosition.value = {lat: coordinates.coords.latitude, lng: coordinates.coords.longitude}
    await fetchPlaces();
    positionLoading.value = false
}

const fetchPlaces = async () => {
    const placesResponse = await placesService.getPlacePredictions({
        input: search.value ?? '',
        sessionToken: placeServiceSessionToken,
        location: new google.maps.LatLng({
            lat: Number(currentPosition.value?.lat ?? 43.842648397350274),
            lng: Number(currentPosition.value?.lng ?? 10.504608228928932),
        }),
        radius: 1000
    });
    if (placesResponse.predictions && placesResponse.predictions.length > 0) {
        const extPlaces: IExtendedPlace[] = [];
        for (const prediction of placesResponse.predictions) {
            const geocoded = await geocoderService.geocode({'placeId': prediction.place_id});
            if (geocoded.results.length > 0) {
                extPlaces.push({
                    prediction: prediction,
                    geocoded: geocoded.results[0]
                })
            }
        }
        return extPlaces
    } else
        return []
}

const onPlaceSelect = async (place: IExtendedPlace) => {
    selected.value = place
}

const clearSelection = () => {
    selected.value = undefined
    results.length = 0;
    mapMarkers.length = 0;
}

watch(() => search.value, debounce(async (newSearch, _) => {
    if (search.value?.length == 0) {
        results.length = 0
        return
    }

    loading.value = true;
    const places = await fetchPlaces()
    mapMarkers.length = 0;
    results.length = 0;
    mapMarkers.push(
        ...places.map((item) => ({
            title: item.prediction.description,
            position: {
                lat: item.geocoded.geometry.location.lat(),
                lng: item.geocoded.geometry.location.lng()
            }
        } as IExtendedMarkerOptions))
    )
    results.push(...places);
    loading.value = false

}, 500));
</script>

<style scoped>
.map-overview[slot='fixed'] {
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 35vh;
}
</style>
